/* The off-air counter: how long until the next broadcast.
 *
 * ONE interval, cleaned up on unmount. Not one per digit, not a
 * requestAnimationFrame loop — this thing runs on a page somebody leaves open
 * for six hours waiting for a show, and it has to cost nothing.
 *
 * It counts against SERVER time when it is given some. /api/live returns
 * `server_time`; the offset between that and the laptop clock is measured once
 * and applied to every tick, so a machine four minutes fast does not tell its
 * owner the show started when it has not. Without a server time it falls back
 * to the local clock, which is right often enough and honest about being the
 * fallback.
 *
 * The digits are NOT a live region. A polite region that updates every second
 * would make a screen reader read a clock over the top of everything else on
 * the page, forever. The digits are aria-hidden and a stable sentence beside
 * them says the same thing once.
 */

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { countdown } from '../format';

export interface CountdownProps {
  /** ISO-8601 UTC. The moment being counted down to. */
  to: string | null | undefined;
  /** `server_time` from /api/live. Without it the local clock is used. */
  from?: string | null;
  /** Fires once, the first tick that reaches zero. Usually a re-poll of /live. */
  onElapsed?: () => void;
  /** What replaces the digits at zero. */
  elapsedLabel?: string;
  /**
   * The sentence a screen reader hears instead of the ticking digits, e.g.
   * "Ground Floor starts at 19:00 Madrid time".
   */
  description?: ReactNode;
  className?: string;
}

export function Countdown({
  to,
  from,
  onElapsed,
  elapsedLabel = 'Starting now',
  description,
  className,
}: CountdownProps) {
  /* Milliseconds to add to Date.now() to get the station's idea of now. Held
     in a ref so re-measuring it does not itself cause a render. */
  const offset = useRef(0);
  const fired = useRef(false);
  const elapsedRef = useRef(onElapsed);
  elapsedRef.current = onElapsed;

  const [text, setText] = useState(() => countdown(to, from ?? undefined));

  useEffect(() => {
    const parsed = from ? Date.parse(from) : Number.NaN;
    offset.current = Number.isNaN(parsed) ? 0 : parsed - Date.now();
  }, [from]);

  useEffect(() => {
    fired.current = false;

    const tick = () => {
      const stationNow = new Date(Date.now() + offset.current);
      const next = countdown(to, stationNow);
      setText(next);

      if (next === '00:00:00' && !fired.current) {
        fired.current = true;
        elapsedRef.current?.();
      }
    };

    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [to]);

  const done = text === '00:00:00';

  return (
    <span className={className ? `cd ${className}` : 'cd'}>
      <span className="cd__digits mono" aria-hidden="true">
        {done ? elapsedLabel : text}
      </span>
      {description ? <span className="vh">{description}</span> : null}
    </span>
  );
}
